import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import DataMixin from 'utils/stores/DataMixin';

import Logger from 'utils/logger';

import API from 'utils/api';
import { PAGE_SIZE } from 'core/constants';
import { EventType } from 'utils/events/constants';
import { getErrorMessage, isNotFound } from 'utils/api/errors';

import {
  MembershipMode,
  MemberGroupDetailResponse,
  MemberGroupHistoryRow,
  RuleVersion,
  UpdateMemberGroupRequest,
} from 'utils/api/modules/memberGroups';
import { RuleDraft, serializeRule } from 'containers/MemberGroups/components/RuleBuilder/registry';

import RootStore from '../Root';

export type UpdateGroupResult = { ok: true } | { ok: false; error: AxiosError };

export const HISTORY_PAGE_SIZE = PAGE_SIZE;

export default class MemberGroupDetailsData extends DataMixin {
  @observable error?: AxiosError;

  @observable group: MemberGroupDetailResponse | null = null;

  @observable activeRule: RuleVersion | null = null;

  @observable membersVersion = 0;

  @observable editing = false;

  @observable converting = false;

  @observable historyEntries: MemberGroupHistoryRow[] = [];

  @observable historyLoaded = false;

  @observable historyLoading = false;

  @observable historyCount = 0;

  @observable historyIsFailure = false;

  private historyVersion = 0;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api);

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadGroup(uuid: string): Promise<void> {
    this.isLoaded = false;
    this.editing = false;
    this.converting = false;
    this.resetHistory();
    try {
      const { data } = await this.api.memberGroups.loadMemberGroup(uuid)();
      runInAction(() => {
        this.error = undefined;
        this.group = data;
        this.activeRule = data.rule;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e as AxiosError;
        this.group = null;
        this.activeRule = null;
      });
      if (!isNotFound(e as AxiosError)) {
        Logger.error(`Invalid load member group API response. Group id: ${uuid}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async updateGroup(patch: UpdateMemberGroupRequest): Promise<UpdateGroupResult> {
    if (!this.group) {
      return { ok: false, error: new AxiosError('No group loaded') };
    }
    try {
      const { data } = await this.api.memberGroups.updateMemberGroup(this.group.uuid, patch)();
      runInAction(() => {
        this.group = data;
        this.invalidateHistory();
      });
      return { ok: true };
    } catch (ex) {
      const error = ex as AxiosError;
      if ((error.response?.status ?? 0) >= 500) {
        Logger.error('Invalid update member group API response.', ex);
      }
      return { ok: false, error };
    }
  }

  @action.bound
  async updateDynamicGroup(changes: {
    name?: string;
    description?: string;
    rule?: RuleDraft;
  }): Promise<UpdateGroupResult> {
    const { rule, ...rest } = changes;
    const patch: UpdateMemberGroupRequest = rule ? { ...rest, rule: serializeRule(rule) } : rest;
    return this.patchAndRehydrate(patch);
  }

  @action.bound
  async switchMembershipMode(mode: MembershipMode, rule?: RuleDraft): Promise<UpdateGroupResult> {
    const patch: UpdateMemberGroupRequest =
      mode === MembershipMode.Dynamic && rule
        ? { membershipMode: mode, rule: serializeRule(rule) }
        : { membershipMode: mode };
    return this.patchAndRehydrate(patch);
  }

  private async patchAndRehydrate(patch: UpdateMemberGroupRequest): Promise<UpdateGroupResult> {
    if (!this.group) {
      return { ok: false, error: new AxiosError('No group loaded') };
    }
    try {
      const { data } = await this.api.memberGroups.updateMemberGroup(this.group.uuid, patch)();
      runInAction(() => {
        this.group = data;
        this.activeRule = data.rule;
        this.membersVersion += 1;
        this.invalidateHistory();
      });
      return { ok: true };
    } catch (ex) {
      const error = ex as AxiosError;
      if ((error.response?.status ?? 0) >= 500) {
        Logger.error('Invalid update member group API response.', ex);
      }
      return { ok: false, error };
    }
  }

  @computed
  get hasMoreHistory(): boolean {
    return this.historyEntries.length < this.historyCount;
  }

  @action.bound
  async loadHistory(offset = 0): Promise<void> {
    if (!this.group || this.historyLoading) {
      return;
    }
    const { uuid } = this.group;
    const version = this.historyVersion;
    runInAction(() => {
      this.historyLoading = true;
      this.historyIsFailure = false;
    });
    try {
      const { data } = await this.api.memberGroups.listHistory(uuid, { limit: HISTORY_PAGE_SIZE, offset })();
      runInAction(() => {
        if (this.group?.uuid !== uuid || this.historyVersion !== version) {
          return;
        }
        this.historyEntries = offset === 0 ? data.results : [...this.historyEntries, ...data.results];
        this.historyCount = data.results.length < HISTORY_PAGE_SIZE ? this.historyEntries.length : data.count;
        this.historyLoaded = true;
      });
    } catch (ex) {
      Logger.error(`Invalid member group history API response. Group id: ${uuid}`, ex);
      if (this.group?.uuid === uuid && this.historyVersion === version) {
        runInAction(() => {
          this.historyIsFailure = true;
        });
        this.store.notification.enqueueErrorSnackbar(getErrorMessage(ex as AxiosError, 'History could not be loaded'));
      }
    } finally {
      runInAction(() => {
        this.historyLoading = false;
      });
    }
  }

  @computed
  get hideMembersWhileEditing(): boolean {
    const isDynamic = this.group ? this.group.membershipMode === MembershipMode.Dynamic : false;
    return this.converting || (this.editing && isDynamic);
  }

  @action.bound
  setEditing(value: boolean): void {
    this.editing = value;
  }

  @action.bound
  startConverting(): void {
    this.converting = true;
    this.editing = false;
  }

  @action.bound
  stopConverting(): void {
    this.converting = false;
  }

  @action.bound
  async deactivate(): Promise<boolean> {
    const result = await this.updateGroup({ active: false });
    if (result.ok) {
      this.store.notification.enqueueSuccessSnackbar('Group deactivated');
      return true;
    }
    this.store.notification.enqueueErrorSnackbar(getErrorMessage(result.error, 'Group could not be deactivated'));
    return false;
  }

  @action.bound
  async reactivate(): Promise<boolean> {
    const result = await this.updateGroup({ active: true });
    if (result.ok) {
      this.membersVersion += 1;
      this.store.notification.enqueueSuccessSnackbar('Group reactivated');
      return true;
    }

    this.store.notification.enqueueErrorSnackbar(getErrorMessage(result.error, 'Group could not be reactivated'));
    return false;
  }

  @action.bound
  async addMembers(uuids: string[]): Promise<boolean> {
    if (!this.group || uuids.length === 0) {
      return false;
    }
    const { uuid } = this.group;
    try {
      await this.postMembers(uuid, uuids);
      const count = uuids.length;
      this.store.notification.enqueueSuccessSnackbar(`Added ${count} ${count === 1 ? 'user' : 'users'}`);
      return true;
    } catch (ex) {
      Logger.error('Invalid add members API response.', ex);
      this.store.notification.enqueueErrorSnackbar(getErrorMessage(ex as AxiosError, 'Members could not be added'));
      return false;
    }
  }

  @action.bound
  async removeMember(userUuid: string, name: string): Promise<void> {
    if (!this.group) {
      return;
    }
    const { uuid } = this.group;
    try {
      await this.api.memberGroups.removeMember(uuid, userUuid)();
      await this.reloadGroup(uuid);
      this.store.notification.enqueueUndoSnackbar(`Removed ${name}`, {
        onUndo: () => {
          this.postMembers(uuid, [userUuid]).catch((ex) => {
            Logger.error('Invalid undo remove member API response.', ex);
            this.store.notification.enqueueErrorSnackbar('Could not undo — please re-add the user');
          });
        },
        autoClose: 6000,
      });
    } catch (ex) {
      Logger.error('Invalid remove member API response.', ex);
      this.store.notification.enqueueErrorSnackbar(getErrorMessage(ex as AxiosError, 'Member could not be removed'));
    }
  }

  private async postMembers(groupUuid: string, uuids: string[]): Promise<void> {
    const { data } = await this.api.memberGroups.addMembers(groupUuid, uuids)();
    runInAction(() => {
      if (this.group?.uuid === groupUuid) {
        this.group = data;
        this.membersVersion += 1;
        this.invalidateHistory();
      }
    });
  }

  private async reloadGroup(uuid: string): Promise<void> {
    const { data } = await this.api.memberGroups.loadMemberGroup(uuid)();
    runInAction(() => {
      if (this.group?.uuid === uuid) {
        this.group = data;
        this.membersVersion += 1;
        this.invalidateHistory();
      }
    });
  }

  @action.bound
  resetHistory(): void {
    this.historyEntries = [];
    this.historyLoaded = false;
    this.historyCount = 0;
    this.historyIsFailure = false;
    this.historyVersion += 1;
  }

  @action.bound
  private invalidateHistory(): void {
    if (this.historyLoaded) {
      this.resetHistory();
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.group = null;
    this.activeRule = null;
    this.membersVersion = 0;
    this.editing = false;
    this.converting = false;
    this.resetHistory();
  }
}
